import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import config from '../config';
import superagent from 'superagent';
import { CookiesProvider } from 'react-cookie';
import { endSessionOnServer, SESSION_ENDED_EVENT } from './session';
/**
 * Name of the HttpOnly session cookie set by the API (VAPT-001/003). Kept for reference only:
 * page scripts can no longer read it, so nothing in the SPA should try to.
 */
export const AUTH_TOKEN = 'sp-auth-token';
export type RequestMessage = { [key: string]: any };
export type RequestHeader = { [key: string]: string };
export type ApiType = 'put' | 'get' | 'post' | 'patch' | 'delete';
import events from 'events';

export type ConnectionApi = {
  send: <T = any>(
    api: ApiType,
    path: string,
    msg?: RequestMessage,
    params?: RequestMessage,
    headers?: any,
  ) => Promise<T>;
  post: <T = any>(
    path: string,
    msg?: RequestMessage,
    params?: RequestMessage,
    headers?: any,
  ) => Promise<T>;
  put: <T = any>(
    path: string,
    msg?: RequestMessage,
    params?: RequestMessage,
    headers?: any,
  ) => Promise<T>;
  get: <T = any>(path: string, params?: RequestMessage, headers?: any) => Promise<T>;
  patch: <T = any>(
    path: string,
    msg?: RequestMessage,
    params?: RequestMessage,
    headers?: any,
  ) => Promise<T>;
  delete: <T = any>(
    path: string,
    msg?: RequestMessage,
    params?: RequestMessage,
    headers?: any,
  ) => Promise<T>;
  /** @deprecated the token is no longer visible to the SPA. `setToken(undefined)` still logs out. */
  setToken: (token?: string) => void;
  /** @deprecated always returns '' - the session lives in an HttpOnly cookie. */
  getToken: () => string;
  /** Replace the current session (after login / self-edit). */
  setSession: (session?: SessionInfo) => void;
  /** True once the server confirmed a session for this browser. */
  hasSession: () => boolean;
  /** True until the initial session check with the server has finished. */
  isSessionLoading: () => boolean;
  /** Re-read the session from the server (rotates the cookie when `renew` is set). */
  refreshSession: (renew?: boolean) => Promise<SessionInfo | undefined>;
  /** Revoke tokens server-side, clear the cookie and local user state. */
  logout: () => Promise<void>;
};

export interface EnumDef {
  id: string;
  type: string;
  name: string;
  tags: string[];
}

export class Enumerations {
  connection: ConnectionApi;
  types: { [type: string]: { [id: string]: EnumDef } } = {};

  constructor(connection: ConnectionApi) {
    this.connection = connection;
  }

  // getAll = async (type: string) => {
  //   try {
  //     if (this.types[type] === undefined) {
  //       const md = await this.connection.get(`admin/masterdata/all/${type}`, {
  //         attributes: ['name', 'tags'],
  //       });
  //       if (md.data.length > 0) {
  //         this.types[type] = Object.fromEntries(md.data.map((d: any) => [d.id, d]));
  //       } else {
  //         return {} as { [id: number]: EnumDef };
  //       }
  //     }
  //     return this.types[type];
  //   } catch (e) {
  //     return {} as { [id: number]: EnumDef };
  //   }
  // };

  // getValue = async (type: string, id: string) => {
  //   if (id === undefined) {
  //     return '';
  //   }
  //   const value = this.types[type]?.[id];
  //   if (value) {
  //     return value;
  //   }
  //   try {
  //     const md = await this.connection.get('admin/masterdata', {
  //       id: [id],
  //       type,
  //       attributes: ['name', 'tags'],
  //     });
  //     if (md.data.length > 0) {
  //       if (!this.types[type]) {
  //         this.types[type] = {};
  //       }
  //       this.types[type][id] = md.data[0].name;
  //     } else {
  //       return `${id}`;
  //     }
  //     return this.types[type][id];
  //   } catch (e) {
  //     return `${id}`;
  //   }
  // };
  //
  // getValues = async (type: string, ids: number[]) => {
  //   const notFound = ids.filter((id) => this.types[type]?.[id] === undefined);
  //   if (notFound.length === 0) {
  //     return ids.map((id) => ({ id, name: this.types[type]?.[id] }));
  //   }
  //   try {
  //     const md = await this.connection.get('admin/masterdata', {
  //       id: ids,
  //       type,
  //       attributes: ['id', 'name'],
  //     });
  //     for (const d of md.data) {
  //       if (!this.types[type]) {
  //         this.types[type] = {};
  //       }
  //       this.types[type][d.id] = d.name;
  //     }
  //     return ids.map((id) => ({ id, name: this.types[type]?.[id] }));
  //   } catch (e) {
  //     return [];
  //   }
  // };
}

export type User = {
  userId: number;
  goalHolderId: number;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  image?: string;
  roles?: string[];
  supervisor: any;
  isLeader: boolean;
};
export type SessionInfo = { user: User; expiresAt: number };
const ApplicationContext = React.createContext<{
  connection?: ConnectionApi;
  user?: User;
  enums: Enumerations;
  events: events.EventEmitter;
}>({} as any);
/** Refresh the cookie this long before the JWT expires (JWT_EXPIRY on the API). */
const REFRESH_LEAD_MS = 15 * 60 * 1000;
const MIN_REFRESH_DELAY_MS = 30 * 1000;

export const ApplicationProvider: FC = (props) => {
  const evts = useRef(new events.EventEmitter()).current;
  const selectValuesRequestCount = useRef(0);
  const [user, setUser] = useState<User | undefined>(undefined);
  const [expiresAt, setExpiresAt] = useState<number | undefined>(undefined);
  const [sessionLoading, setSessionLoading] = useState<boolean>(true);
  const userRef = useRef<User | undefined>(undefined);
  userRef.current = user;

  const applySession = useCallback((session?: SessionInfo) => {
    if (session?.user?.userId) {
      const u = session.user;
      setUser({ ...u, fullName: u.fullName ?? `${u.firstName} ${u.lastName}` });
      setExpiresAt(session.expiresAt);
    } else {
      setUser(undefined);
      setExpiresAt(undefined);
    }
  }, []);

  /**
   * Session bootstrap / renewal. The JWT is an HttpOnly cookie the SPA cannot read, so the
   * user profile comes from the API instead of jwt-decode. `renew` rotates the cookie.
   */
  const refreshSession = useCallback(
    async (renew?: boolean): Promise<SessionInfo | undefined> => {
      const path =
        renew && userRef.current
          ? `users/refresh-token/${userRef.current.userId}`
          : 'users/session';
      try {
        const res = await superagent
          .get(`${config.api.baseUrl}/${path}`)
          .withCredentials()
          .set('Content-Type', 'application/json');
        const session = res.body as SessionInfo;
        applySession(session);
        return session;
      } catch (e) {
        applySession(undefined);
        return undefined;
      } finally {
        setSessionLoading(false);
      }
    },
    [applySession],
  );

  // Initial check on load (restores the session after a reload).
  useEffect(() => {
    refreshSession(false);
  }, [refreshSession]);

  // Silent renewal shortly before expiry (VAPT-006: short-lived JWT without re-login).
  useEffect(() => {
    if (!user || !expiresAt) {
      return;
    }
    const delay = Math.max(expiresAt - Date.now() - REFRESH_LEAD_MS, MIN_REFRESH_DELAY_MS);
    const t = setTimeout(() => refreshSession(true), delay);
    return () => clearTimeout(t);
  }, [user, expiresAt, refreshSession]);

  // Session ended from outside the provider (MSAL interaction-required, login failure).
  useEffect(() => {
    const onEnded = () => applySession(undefined);
    window.addEventListener(SESSION_ENDED_EVENT, onEnded);
    return () => window.removeEventListener(SESSION_ENDED_EVENT, onEnded);
  }, [applySession]);

  const logout = useCallback(async () => {
    applySession(undefined);
    await endSessionOnServer();
  }, [applySession]);

  const updateToken = useCallback(
    (token?: string) => {
      // Legacy signature. The SPA never receives a token any more; only "clear" is meaningful.
      if (!token) {
        logout();
      }
    },
    [logout],
  );

  const sendRequest = useCallback(
    (
      method: (url: string) => superagent.SuperAgentRequest,
      path: string,
      msg?: RequestMessage,
      query?: RequestMessage,
      headers?: RequestHeader,
    ) => {
      return new Promise((resolve, reject) => {
        const isSelectValuesRequest = path.startsWith('select-values/');
        if (isSelectValuesRequest) {
          selectValuesRequestCount.current += 1;
          evts.emit('SELECT_VALUES_LOADING', selectValuesRequestCount.current);
        }
        const url = `${config.api.baseUrl}`;
        // alert(`${JSON.stringify(query)}`);
        const req = method(`${url}/${path}`).set('Content-Type', 'application/json');
        if (query) {
          req.query(query);
        }
        // Auth is the HttpOnly session cookie (VAPT-003): send credentials, no Bearer header.
        req.withCredentials();
        if (headers) {
          Object.keys(headers).forEach((key) => req.set(key, headers[key]));
        }

        let jsonString = '';
        try {
          jsonString = JSON.stringify(msg);
        } catch (e) {
          throw Error('Invalid JSON object');
        }

        req
          .send(jsonString)
          .then((res: any) => {
            if (isSelectValuesRequest) {
              selectValuesRequestCount.current = Math.max(0, selectValuesRequestCount.current - 1);
              evts.emit('SELECT_VALUES_LOADING', selectValuesRequestCount.current);
            }
            // console.error('SUCCESS', JSON.stringify(res, null, 2));
            resolve(res.body);
          })
          .catch((res: any) => {
            if (isSelectValuesRequest) {
              selectValuesRequestCount.current = Math.max(0, selectValuesRequestCount.current - 1);
              evts.emit('SELECT_VALUES_LOADING', selectValuesRequestCount.current);
            }
            if (res.response) {
              const { response } = res;
              if (response.status === 401 && userRef.current) {
                // Token expired or revoked: drop the local session so route guards redirect to login.
                applySession(undefined);
              }
              if (response.statusCode) {
                const { status, statusText } = response;
                if (res.response.body?.message) {
                  reject({
                    status,
                    statusText: statusText ?? `${status}`,
                    message: res.response.body?.message,
                    body: res.response.body,
                  });
                } else {
                  reject({
                    status,
                    statusText: statusText ?? `${status}`,
                    message: statusText ? statusText : 'Unknown',
                    body: res.response.body,
                  });
                }
              } else {
                reject({
                  statusCode: 500,
                  statusText: 'Unknown',
                  code: 'Unknown',
                  message: 'Unknown',
                });
              }
            }
          });
      }) as any;
    },
    [evts, applySession],
  );

  const post = useCallback(
    (path: string, msg?: RequestMessage, query?: RequestMessage, headers?: RequestHeader) => {
      return sendRequest(superagent.post, path, msg, query, headers);
    },
    [sendRequest],
  );
  const put = useCallback(
    (path: string, msg?: RequestMessage, query?: RequestMessage, headers?: RequestHeader) => {
      return sendRequest(superagent.put, path, msg, query, headers);
    },
    [sendRequest],
  );
  const get = useCallback(
    (path: string, query?: RequestMessage, headers?: RequestHeader) => {
      return sendRequest(superagent.get, path, undefined, query, headers);
    },
    [sendRequest],
  );
  const patch = useCallback(
    (path: string, msg?: RequestMessage, query?: RequestMessage, headers?: RequestHeader) => {
      return sendRequest(superagent.patch, path, msg, query, headers);
    },
    [sendRequest],
  );
  const del = useCallback(
    (path: string, msg?: RequestMessage, query?: RequestMessage, headers?: RequestHeader) => {
      return sendRequest(superagent.delete, path, msg, query, headers);
    },
    [sendRequest],
  );

  const send = useCallback(
    (api: ApiType, path: string, msg?: RequestMessage, params?: RequestMessage, headers?: any) => {
      switch (api) {
        case 'post':
          return post(path, msg, params, headers);
        case 'get':
          return get(path, params ?? msg, headers);
        case 'put':
          return put(path, msg, params, headers);
        case 'patch':
          return patch(path, msg, params, headers);
        case 'delete':
          return del(path, msg, params, headers);
      }
    },
    [del, get, post, put, patch],
  );

  const getToken = useCallback(() => '', []);
  const hasSession = useCallback(() => !!userRef.current, []);
  const isSessionLoading = useCallback(() => sessionLoading, [sessionLoading]);
  const connection: ConnectionApi = useMemo(
    () => ({
      send,
      post,
      put,
      get,
      patch,
      delete: del,
      setToken: updateToken,
      getToken,
      setSession: applySession,
      hasSession,
      isSessionLoading,
      refreshSession,
      logout,
    }),
    [
      send,
      post,
      put,
      get,
      patch,
      del,
      updateToken,
      getToken,
      applySession,
      hasSession,
      isSessionLoading,
      refreshSession,
      logout,
    ],
  );
  const enums = useMemo(() => new Enumerations(connection), [connection]);
  return (
    <CookiesProvider>
      <ApplicationContext.Provider
        value={{
          connection,
          user,
          enums,
          events: evts,
        }}>
        {props.children}
      </ApplicationContext.Provider>
    </CookiesProvider>
  );
};

export default ApplicationContext;
